import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import { API_ORGANIZATION_ID } from "config/headers";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AdminListRequestOfTemplateFilter,
  IListResponseOfTemplateAdmin,
  ListResponseOfTemplateAdmin,
  TemplateFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { TemplateTypeIds } from "types/common-types";

interface IListResponseOfTemplateWithId extends IListResponseOfTemplateAdmin {
  id: string;
}

const CertificateList = () => {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const {
    templateService,
    contentTemplateCreateUrl,
    contentTemplateDetailsUrl,
  } = useGlobalContext();

  const { organizationId } = contextStore.useState();

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.templateSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  useListTitle("templatesLabel");

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfTemplateAdmin>(
    [
      "contentTemplates",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
    ],
    () =>
      templateService.getTemplates(
        AdminListRequestOfTemplateFilter.fromJS({
          organizationId:
            organizationId?.toLowerCase() === API_ORGANIZATION_ID?.toLowerCase()
              ? undefined
              : organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: TemplateFilter.fromJS({
            searchText: debouncedSearchTerm,
            excludedTemplateTypeIds: [TemplateTypeIds.SkuAgreements],
          }),
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId]);

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "templateTypeName",
      customSortAccessor: "templateType.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "typeLabel" }),
    },
    {
      accessor: "templateEntityName",
      customSortAccessor: "templateEntity.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "entityLabel" }),
    },
    {
      accessor: "placeholderName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "placeholderLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
    },
  ];

  const templates = data?.data as IListResponseOfTemplateWithId[];

  const onClick = (id: string) => {
    queryClient.invalidateQueries(["template", id]);
    navigate(contentTemplateDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link
          to={contentTemplateCreateUrl()}
          style={{ textDecoration: "none" }}
        >
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    persistanceStore.update((s) => {
                      s.templateSearchTerm = "";
                    })
                  }
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            persistanceStore.update((s) => {
              s.templateSearchTerm = e.target.value;
            });
          }}
          data-cy="searchText"
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: templates ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "templatesLabel" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              contentTemplateDetailsUrl(id)
            ),
          ],
        }}
      />
    </Container>
  );
};

export default CertificateList;
